import { css, type SerializedStyles } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';
import { TOOLS_CONTROL_HEIGHT } from '../KaiNavigator/navigatorStyles';

/** Also the fallback width used to place the menu before it has been measured. */
const MENU_MIN_WIDTH = 285;

export type ScopeFilterStyles = {
  menuMinWidth: number;
  activeIndicator: SerializedStyles;
  filterButton: SerializedStyles;
  container: SerializedStyles;
  menu: SerializedStyles;
  menuHeader: SerializedStyles;
  menuContent: SerializedStyles;
  group: SerializedStyles;
  divider: SerializedStyles;
  heading: SerializedStyles;
  stage: SerializedStyles;
  disabledStage: SerializedStyles;
  stageHeading: SerializedStyles;
  rows: SerializedStyles;
  compactRows: SerializedStyles;
  row: SerializedStyles;
  emptyRow: SerializedStyles;
  selectedRow: SerializedStyles;
  label: SerializedStyles;
  count: SerializedStyles;
  emptyCount: SerializedStyles;
  footer: SerializedStyles;
  taskDetails: SerializedStyles;
  resetButton: SerializedStyles;
};

export const DEFAULT_SCOPE_FILTER_STYLES: ScopeFilterStyles = {
  menuMinWidth: MENU_MIN_WIDTH,
  activeIndicator: css`
    label: ACTIVE_INDICATOR;
    position: absolute;
    top: -6px;
    right: -6px;
    width: 12px;
    height: 12px;
    padding: 0 3px;
    background: ${COLORS.error};
    border: 1.5px solid ${COLORS.white};
    border-radius: 50%;
  `,
  filterButton: css`
    label: SCOPE_ATTENTION_FILTER_BUTTON;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: ${TOOLS_CONTROL_HEIGHT}px;
    padding: 0 10px;
    border: 1px solid ${COLORS.stroke};
    background: transparent;
    color: ${COLORS.textSecondary};
    font-family: ${FONT.body};
    font-size: ${pxToRem(11)};
    font-weight: ${WEIGHT.bold};
    letter-spacing: 0.02em;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.12s, border-color 0.12s, background 0.12s;
  
    &:hover { color: ${COLORS.ink}; border-color: ${COLORS.ink}; }
    svg { width: 13px; height: 13px; stroke: currentColor; fill: none; flex-shrink: 0; }

    &[data-active] { color: ${COLORS.ink}; border-color: ${COLORS.ink}; }
  `,
  container: css`
    label: SCOPE_FILTER_CONTAINER;
    position: relative;
    flex: 0 0 auto;
    margin-left: auto;
    display: inline-flex;
    align-items: stretch;
  `,
  menu: css`
    label: SCOPE_FILTER_MENU;
    position: fixed;
    width: ${MENU_MIN_WIDTH}px;
    min-width: ${MENU_MIN_WIDTH}px;
    max-width: 460px;
    max-height: calc(100vh - 16px);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    overflow: hidden;
    z-index: 250;
    background: ${COLORS.white};
    border: 1px solid ${COLORS.stroke};
    box-shadow: 0 8px 24px ${withOpacity(COLORS.ink, 0.14)};
    padding: 6px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(FONT_SIZE.bodyDefault)};
    color: ${COLORS.ink};
  `,
  menuHeader: css`
    label: SCOPE_FILTER_MENU_HEADER;
    position: sticky;
    top: 0;
    z-index: 1;
    flex: 0 0 auto;
    padding: 4px 10px 12px;
    background: ${COLORS.white};
    font-size: ${pxToRem(12)};
    font-weight: ${WEIGHT.bold};
    letter-spacing: 0.1em;
    text-transform: uppercase;
  `,
  menuContent: css`
    label: SCOPE_FILTER_MENU_CONTENT;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  `,
  group: css`
    label: SCOPE_FILTER_GROUP;
    margin-bottom: 12px;
  `,
  divider: css`
    label: SCOPE_FILTER_GROUP_DIVIDER;
    border-top: 1px solid ${COLORS.stroke};
    padding-top: 8px;
  `,
  heading: css`
    label: SCOPE_FILTER_GROUP_HEADING;
    font-size: ${pxToRem(12)};
    font-weight: ${WEIGHT.medium};
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 8px 10px 12px;
  `,
  stage: css`
    label: SCOPE_FILTER_STAGE;
    padding-bottom: 8px;

    &:last-child { padding-bottom: 0; }
  `,
  disabledStage: css`
    label: SCOPE_FILTER_DISABLED_STAGE;
    opacity: 0.45;
  `,
  stageHeading: css`
    label: SCOPE_FILTER_STAGE_HEADING;
    padding: 4px 10px 8px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(12)};
    font-weight: ${WEIGHT.medium};
    letter-spacing: 0.1em;
    line-height: 0.917;
    color: ${COLORS.midtone};
  `,
  rows: css`
    label: SCOPE_FILTER_ROWS;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  compactRows: css`
    label: SCOPE_FILTER_COMPACT_ROWS;
    display: flex;
    flex-direction: column;
    gap: 4px;
  `,
  row: css`
    label: SCOPE_FILTER_ROW;
    display: flex;
    align-items: center;
    gap: 7px;
    width: 100%;
    padding: 4px 10px;
    font-family: ${FONT.body};
    font-size: ${pxToRem(15)};
    line-height: 1.067;
    color: ${COLORS.ink};
    text-align: left;
    border: 0;
    background: none;
    cursor: pointer;
    transition: background 0.1s;

    &:hover { background: ${COLORS.sunken}; }

    &:disabled {
      cursor: not-allowed;
    }
  `,
  emptyRow: css`
    label: SCOPE_FILTER_EMPTY_ROW;
    color: ${COLORS.midtone};
  `,
  selectedRow: css`
    label: SCOPE_FILTER_SELECTED_ROW;
    background: ${COLORS.selectedWash};

    &:hover { background: ${COLORS.selectedWash}; }
  `,
  label: css`
    label: SCOPE_FILTER_LABEL;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
  `,
  count: css`
    label: SCOPE_FILTER_COUNT;
    font-size: ${pxToRem(15)};
    font-weight: ${WEIGHT.bold};
    color: ${COLORS.textSecondary};
  `,
  emptyCount: css`
    label: SCOPE_FILTER_EMPTY_COUNT;
    color: ${COLORS.textTertiary};
    opacity: 0.6;
  `,
  footer: css`
    label: SCOPE_FILTER_FOOTER;
    position: sticky;
    bottom: 0;
    z-index: 1;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px 4px;
    margin-top: 4px;
    border-top: 1px solid ${COLORS.stroke};
  `,
  taskDetails: css`
    label: SCOPE_FILTER_TASK_DETAILS;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(FONT_SIZE.monoCaption)};
    letter-spacing: 0.03em;
    color: ${COLORS.textTertiary};
  `,
  resetButton: css`
    label: SCOPE_FILTER_RESET_BUTTON;
    font-family: ${FONT.body};
    font-size: ${pxToRem(11)};
    font-weight: ${WEIGHT.bold};
    color: ${COLORS.error};
    background: none;
    border: 0;
    cursor: pointer;
    padding: 0;

    &:disabled { color: ${COLORS.textTertiary}; cursor: default; }
  `,
};
